"use client";

import Link from "next/link";
import { useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/lib/supabase";
import { SHARE_BONUS } from "@/lib/tokens";
import { TOKEN_PACKAGES } from "@/lib/tokenPackages";

// The shared body of the tokens UI, used by both the full /tokens page and the
// compact in-game panel. `compact` switches the package layout from big cards
// to tight rows so it fits the narrow panel.
export default function TokensContent({ compact = false }: { compact?: boolean }) {
  const { user, profile, guestTokens } = useAuth();
  // The package id currently being purchased (button spinner), or null.
  const [pending, setPending] = useState<string | null>(null);
  const [error, setError] = useState("");

  // Starts Stripe Checkout for a package: ask our API for a session, then send
  // the browser to the Stripe-hosted checkout page.
  const handlePurchase = async (packageId: string) => {
    if (pending) return;
    setPending(packageId);
    setError("");
    try {
      // Session lives in localStorage, so pass the access token to the API.
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const res = await fetch("/api/stripe/checkout", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session?.access_token ?? ""}`,
        },
        body: JSON.stringify({ packageId }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.error || !data.url) {
        setError("Something went wrong. Try again.");
        setPending(null);
        return;
      }
      window.location.href = data.url as string;
    } catch {
      setError("Something went wrong. Try again.");
      setPending(null);
    }
  };

  // Guests have a real (localStorage) balance: they start with some, earn by
  // sharing, but can't buy. The pitch is to sign in for a bonus + daily rewards.
  if (!user) {
    return (
      <div className="flex flex-col gap-4">
        <div className="rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-center text-sm text-white/60">
          Your balance:{" "}
          <span className="font-mono font-bold text-white">
            {(guestTokens ?? 0).toLocaleString()}
          </span>{" "}
          tokens
        </div>
        <div className="rounded-xl border border-accent/30 bg-accent/5 px-4 py-3 text-center text-sm text-white/70">
          Earn tokens by sharing your result{" "}
          <span className="font-bold text-accent">+{SHARE_BONUS} each</span>
        </div>
        <div className="rounded-2xl border border-white/10 bg-white/5 p-5 text-center">
          <p className="text-sm text-white/70">
            Sign in for{" "}
            <span className="font-bold text-accent">+25 tokens</span>, keep your
            balance, and unlock escalating daily login rewards.
          </p>
          <Link
            href="/login"
            className="mt-4 inline-block rounded-full bg-accent px-6 py-2.5 text-sm font-bold uppercase tracking-widest text-white transition hover:brightness-110"
          >
            Sign In
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-center text-sm text-white/60">
        Your balance:{" "}
        <span className="font-mono font-bold text-white">
          {(profile?.tokens ?? 0).toLocaleString()}
        </span>{" "}
        tokens
      </div>

      {compact ? (
        <div className="flex flex-col gap-2">
          {TOKEN_PACKAGES.map((pkg) => (
            <div
              key={pkg.id}
              className="flex items-center justify-between rounded-lg border border-white/10 bg-white/5 px-3 py-2"
            >
              <div className="text-sm">
                <span className="font-mono font-extrabold text-accent">
                  {pkg.tokens.toLocaleString()}
                </span>
                <span className="ml-1 text-xs uppercase tracking-widest text-white/40">
                  tokens
                </span>
                <span className="ml-2 font-bold">{pkg.price}</span>
              </div>
              <button
                onClick={() => handlePurchase(pkg.id)}
                disabled={pending !== null}
                className="rounded-full bg-accent px-4 py-1.5 text-xs font-bold uppercase tracking-widest text-white transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {pending === pkg.id ? "…" : "Buy"}
              </button>
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          {TOKEN_PACKAGES.map((pkg) => (
            <div
              key={pkg.id}
              className="flex flex-col items-center rounded-2xl border border-white/10 bg-white/5 p-6 text-center"
            >
              <div className="text-[clamp(2rem,6vw,3rem)] font-extrabold text-accent">
                {pkg.tokens.toLocaleString()}
              </div>
              <div className="text-xs font-bold uppercase tracking-widest text-white/40">
                Tokens
              </div>
              <div className="mt-3 text-xl font-bold">{pkg.price}</div>
              <button
                onClick={() => handlePurchase(pkg.id)}
                disabled={pending !== null}
                className="mt-5 w-full rounded-full bg-accent px-6 py-3 text-sm font-bold uppercase tracking-widest text-white transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {pending === pkg.id ? "Starting…" : "Buy"}
              </button>
            </div>
          ))}
        </div>
      )}

      {error && (
        <div className="rounded-xl border border-red-500/40 bg-red-500/10 px-4 py-3 text-center text-sm font-semibold text-red-300">
          {error}
        </div>
      )}

      <div className="rounded-xl border border-accent/30 bg-accent/5 px-4 py-3 text-center text-sm text-white/70">
        Earn free tokens: Share your result{" "}
        <span className="font-bold text-accent">+{SHARE_BONUS} tokens</span>
      </div>
    </div>
  );
}
